import React, { useState } from "react";
import { Alert, Box } from "@mui/material";
import Badge from "components/basic/badge";
import Button from "components/basic/button";
import LoadingSpinner from "components/basic/loadingSpinner";
import Switch from "components/basic/switch";
import Table, { ColumnProps } from "components/basic/table";
import Typography from "components/basic/typography";

import ListToolbar from "../ListToolbar";
import Pager from "../Pager";
import UserDetail from "./components/UserDetail";
import { useAdminUsers } from "../../adminApi";
import { formatDate } from "../../helpers";
import { AdminUser } from "../../types";

/** EV owners and portal clients; vendor team accounts live under Vendors. */
const UsersTab: React.FC = () => {
  const [search, setSearch] = useState("");
  const [suspendedOnly, setSuspendedOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { users, total, loading, error, refresh } = useAdminUsers(
    search,
    suspendedOnly,
    page,
  );
  const selected = users.find((u) => u.id === selectedId) ?? null;

  const columns: ColumnProps<AdminUser>[] = [
    {
      header: "Name",
      align: "left",
      card: "primary",
      cell: (u) => (
        <Box>
          <Typography variant="body1" style={{ fontWeight: 600 }}>
            {u.account.firstName} {u.account.lastName}
          </Typography>
          <Typography variant="caption">{u.account.email}</Typography>
        </Box>
      ),
    },
    {
      header: "Source",
      cell: (u) =>
        u.portalVendorName ? `Portal: ${u.portalVendorName}` : "Marketplace",
    },
    {
      header: "Joined",
      cell: (u) => formatDate(u.account.createdAt),
    },
    {
      header: "Status",
      card: "inline",
      cell: (u) =>
        u.suspendedAt ? (
          <Badge label="Suspended" color="#d32f2f" />
        ) : u.isAdmin ? (
          <Badge label="Admin" color="#1976d2" />
        ) : (
          <Badge label="Active" color="#2e7d32" />
        ),
    },
    {
      header: "",
      card: "actions",
      cell: (u) => (
        <Button size="small" onClick={() => setSelectedId(u.id)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <ListToolbar
        placeholder="Search name or email"
        onSearch={(value) => {
          setSearch(value);
          setPage(0);
        }}
      >
        <Switch
          label="Suspended only"
          checked={suspendedOnly}
          onChange={(checked) => {
            setSuspendedOnly(checked);
            setPage(0);
          }}
        />
      </ListToolbar>

      {error ? (
        <Box px={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : loading ? (
        <LoadingSpinner />
      ) : users.length === 0 ? (
        <Box p={4} textAlign="center">
          <Typography variant="body2">No users match.</Typography>
        </Box>
      ) : (
        <>
          <Table columns={columns} data={users} />
          <Pager page={page} total={total} onPageChange={setPage} />
        </>
      )}

      {selected && (
        <UserDetail
          user={selected}
          onChanged={refresh}
          onClose={() => setSelectedId(null)}
        />
      )}
    </Box>
  );
};

export default UsersTab;
